import { useEffect, useRef, useState } from 'react';
import { formatTime, toBerlinDateKey } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow } from '../../ds/components';
import { useEscapeKey, useFocusTrap } from '../../ds/hooks';
import type { ConflictDetails, ConflictEntry, EventType, Slot } from './api';
import { formatPanelTitle, formatSlotRange, formatTimeRange } from './dates';
import styles from './ConflictView.module.css';

export interface ConflictViewProps {
  details: ConflictDetails;
  type: EventType;
  contactName: string;
  /** Whether „Trotzdem anlegen" is offered (admins, `settings.organization = yes`). */
  canOverride: boolean;
  pending: boolean;
  error: string | null;
  onPick: (slot: Slot) => void;
  onOverride: () => void;
  onBack: () => void;
}

/** „16:00 – 16:45 Videoberatung mit M. Osterloh läuft bereits. Der Puffer von 15 Minuten danach ist ebenfalls betroffen." */
export function conflictSentence(conflict: ConflictEntry, bufferAfterMinutes: number): string {
  const range = formatTimeRange(conflict.startsAt, conflict.endsAt);
  const first =
    conflict.kind === 'appointment'
      ? `${range} ${conflict.title} läuft bereits.`
      : conflict.kind === 'hold'
        ? `${range} ist gerade für eine Buchung reserviert.`
        : `${range} ist im verbundenen Kalender belegt (${conflict.title}).`;
  if (conflict.kind === 'appointment' && bufferAfterMinutes > 0) {
    return `${first} Der Puffer von ${bufferAfterMinutes} Minuten danach ist ebenfalls betroffen.`;
  }
  return first;
}

interface OverviewRow {
  key: string;
  kind: 'busy' | 'requested' | 'free';
  startsAt: string;
  endsAt: string;
  title: string;
  note: string;
}

/** The rows of „Betroffener Tag": the day's busy intervals, the requested one, the next free slot of that day. */
export function dayOverviewRows(
  details: ConflictDetails,
  contactName: string,
  requestedEndsAt: string,
): OverviewRow[] {
  const day = toBerlinDateKey(details.requested.startsAt);
  const overlap = Math.max(...details.conflicts.map((entry) => entry.overlapMinutes), 0);
  const rows: OverviewRow[] = details.dayOverview.map((entry) => ({
    key: `busy-${entry.startsAt}-${entry.title}`,
    kind: 'busy',
    startsAt: entry.startsAt,
    endsAt: entry.endsAt,
    title: entry.title,
    note: entry.kind === 'appointment' ? 'Bestätigt' : entry.kind === 'hold' ? 'Reserviert' : 'Belegt',
  }));
  rows.push({
    key: 'requested',
    kind: 'requested',
    startsAt: details.requested.startsAt,
    endsAt: requestedEndsAt,
    title: `Neuer Termin · ${contactName}`,
    note: `Überschneidung ${overlap} Min`,
  });
  const nextFree = details.alternatives.find((slot) => toBerlinDateKey(slot.startsAt) === day);
  if (nextFree) {
    rows.push({
      key: `free-${nextFree.startsAt}`,
      kind: 'free',
      startsAt: nextFree.startsAt,
      endsAt: nextFree.endsAt,
      title: 'Frei',
      note: '',
    });
  }
  return rows.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}

/** Beratung 03 — the conflict screen: notice, the next free times, the day's overview. */
export function ConflictView({
  details,
  type,
  contactName,
  canOverride,
  pending,
  error,
  onPick,
  onOverride,
  onBack,
}: ConflictViewProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [chosen, setChosen] = useState<Slot | null>(details.alternatives[0] ?? null);
  useEffect(() => {
    setChosen(details.alternatives[0] ?? null);
  }, [details]);
  useFocusTrap(cardRef, true, { initialFocus: 'container' });
  useEscapeKey(onBack, true);
  const first = details.conflicts[0];
  const rows = dayOverviewRows(details, contactName, details.requested.endsAt);

  return (
    <div className={styles.stage}>
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-title"
        tabIndex={-1}
        className={styles.card}
      >
        <div>
          <Eyebrow>Neuer Termin</Eyebrow>
          <div id="conflict-title" className={styles.title}>
            {formatPanelTitle(details.requested.startsAt)}
          </div>
        </div>
        <div className={styles.warning} role="alert">
          <div className={styles.warningTitle}>Diese Zeit ist doppelt belegt</div>
          <div className={styles.warningText}>
            {first
              ? conflictSentence(first, type.bufferAfterMinutes)
              : 'Die gewählte Zeit ist bereits belegt.'}
          </div>
        </div>
        <div>
          <div className={styles.listLabel}>Nächste freie Zeiten</div>
          {details.alternatives.length === 0 ? (
            <div className={styles.emptyAlternatives}>Keine freien Zeiten in den nächsten Tagen.</div>
          ) : (
            <div className={styles.list} role="radiogroup" aria-label="Nächste freie Zeiten">
              {details.alternatives.map((slot, index) => {
                const selected = chosen?.startsAt === slot.startsAt;
                return (
                  <button
                    key={slot.startsAt}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={clsx(styles.alternative, selected && styles.alternativeSelected)}
                    onClick={() => setChosen(slot)}
                  >
                    <span className={styles.alternativeTime}>
                      {formatSlotRange(slot.startsAt, slot.endsAt)}
                    </span>
                    {index === 0 ? <span className={styles.recommended}>Empfohlen</span> : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.actions}>
          {chosen ? (
            <Button className={styles.primary} loading={pending} onClick={() => onPick(chosen)}>
              {formatTime(chosen.startsAt)} übernehmen
            </Button>
          ) : (
            <Button className={styles.primary} variant="secondary" onClick={onBack}>
              Andere Zeit wählen
            </Button>
          )}
          <Button
            variant="secondary"
            className={styles.secondary}
            disabled={!canOverride}
            loading={pending && canOverride}
            onClick={onOverride}
          >
            Trotzdem anlegen
          </Button>
        </div>
        <div className={styles.footnote}>
          „Trotzdem anlegen“ ist Admins vorbehalten und wird im Deal-Protokoll vermerkt.
        </div>
        <button type="button" className={styles.backLink} onClick={onBack}>
          ‹ Zurück zum Termin
        </button>
      </div>
      <div className={styles.side}>
        <div className={styles.overview}>
          <Eyebrow>Betroffener Tag</Eyebrow>
          <div className={styles.overviewRows}>
            {rows.map((row) => (
              <div
                key={row.key}
                className={clsx(
                  styles.row,
                  row.kind === 'busy' && styles.rowBusy,
                  row.kind === 'requested' && styles.rowRequested,
                  row.kind === 'free' && styles.rowFree,
                )}
              >
                <span className={styles.rowTime}>{formatTimeRange(row.startsAt, row.endsAt)}</span>
                <span className={styles.rowTitle}>{row.title}</span>
                {row.note ? <span className={styles.rowNote}>{row.note}</span> : null}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.explainer}>
          Konflikte kommen aus dem verbundenen Google- oder Microsoft-Kalender (FreeBusy) sowie aus DAMOKLES
          selbst. Ohne verbundenen Kalender prüft DAMOKLES nur die eigenen Termine.
        </div>
      </div>
    </div>
  );
}
